/* eslint-disable no-tabs */
import CONDITION_TYPES from './types';
import { conditionMatches } from './utils';
import useParse from '../parse';
import { useSelector } from 'react-redux';
import useUser from '../user';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import _ from 'lodash';

/*
    Evaluates conditions that are used by action sequence jumps and conditional display of element

    Fields:
        conditionType:
            options: local, document, json, hasStripeRole, doesNotHaveStripeRole, hasRole, doesNotHaveRole, hasClaim, doesNotHaveClaim, loggedin, notloggedin, hasProductId, 
	 			doesNotHaveProductId, 	hasActiveSubscription, doesNotHaveActiveSubscription
        
        conditionValueSource
	        used when conditionType IN ['hasStripeRole','doesNotHaveStripeRole','hasRole','doesNotHaveRole','hasClaim',
		        'doesNotHaveClaim','hasProductId','doesNotHaveProductId','hasActiveSubscription','doesNotHaveActiveSubscription']

	        options: static, local, datalink

        conditionValueDataLinkId
	        used when conditionValueSource = 'datalink'

        conditionValueDataLinkField
	        used when conditionValueSource = 'datalink'

        conditionDataLinkId
	        used when conditionType = 'document'

        conditionDataLinkField
	        used when conditionType = 'document'

        conditionDataLinkOperator
	        used when conditionType = 'document'

        conditionDataLinkValue
	        used when conditionType = 'document'

        conditionJson
	        used when conditionType = 'json'

        conditionLocalValueField
	        used when conditionValueSource = 'local'

        conditionLocalField
	        used when conditionType = 'local'

        conditionLocalOperator
	        used when conditionType = 'local'

        conditionLocalValue
	        used when conditionType = 'local'

        conditionStaticValue
	        used when conditionValueSource = 'static'
*/
const useConditions = () => {
  const user = useSelector(state => state.User);
  const [authenticatedUser, authenticatedUserLoading] = useAuthState(auth);
  const { processing, results: actionSequencerResults } = useSelector(
    state => state.ActionSequences
  );
  const { parse } = useParse();
  const { emailIsUnique } = useUser();

  const hasProductOwnership = () => {
    return false;
  };
  const hasActiveSubscription = () => {
    return false;
  };

  const evaluate = async ({
    condition,
    dataLinks,
    documents,
    selections,
    local,
    history,
    sequencer
  }) => {
    switch (condition.conditionType) {
      case CONDITION_TYPES.ACTION_SEQUENCE_RUNNING: {
        return sequencer ? sequencer.processing : processing;
      }
      case CONDITION_TYPES.ACTION_SEQUENCE_NOT_RUNNING: {
        return sequencer ? !sequencer.processing : !processing;
      }
      case CONDITION_TYPES.ACCESS_NONE:
      case CONDITION_TYPES.ACCESS_READONLY:
      case CONDITION_TYPES.ACCESS_READWRITE: {
        const contentType = dataLinks.find(
          d => d.id === condition.conditionDataLinkId
        )?.contentType;

        if (contentType.roles.length === 0) {
          if (condition.conditionType === CONDITION_TYPES.ACCESS_NONE) {
            return false;
          }
          return true;
        }

        if (condition.conditionType === CONDITION_TYPES.ACCESS_NONE) {
          return contentType.roles?.some(
            role => role.role === user?.role && role.accessLevel === 'no_access'
          );
        } else if (
          condition.conditionType === CONDITION_TYPES.ACCESS_READONLY
        ) {
          return contentType.roles?.some(
            role => role.role === user?.role && role.accessLevel === 'read'
          );
        } else {
          return contentType.roles?.some(
            role =>
              role.role === user?.role && role.accessLevel === 'read_write'
          );
        }
      }
      case CONDITION_TYPES.HAS_UNIQUE_EMAIL: {
        return {
          matched: await emailIsUnique(
            condition.conditionStaticValue || user.email
          )
        };
      }
      case CONDITION_TYPES.HAS_VERIFIED_PHONE: {
        return {
          matched:
            user?.session?.phoneNumber !== undefined &&
            user?.session?.phoneNumber?.trim().length > 0 &&
            user?.session?.phoneVerified
        };
      }
      case CONDITION_TYPES.HAS_VERIFIED_EMAIL: {
        return { matched: user?.session?.emailVerified };
      }
      case CONDITION_TYPES.DATALINK: {
        return await evaluateDataLinkCondition({
          condition,
          user,
          dataLinks,
          documents,
          selections,
          history
        });
      }
      case CONDITION_TYPES.VALIDATION: {
        return await evaluateValidationCondition({
          condition,
          user,
          documents,
          selections,
          history
        });
      }
      case CONDITION_TYPES.FORM_VALUE_IS_MATCHED: {
        return await evaluateFormValueCondition({
          condition,
          user,
          documents,
          selections,
          history
        });
      }
      case CONDITION_TYPES.LOCAL: {
        return await evaluateLocalStoreCondition({
          condition,
          documents,
          selections,
          local,
          history
        });
      }
      case CONDITION_TYPES.ACTION_SEQUENCER_RESULTS: {
        return await evaluateActionSequencerResultsCondition({
          condition,
          documents,
          selections,
          local,
          history,
          sequencer
        });
      }
      case CONDITION_TYPES.JSON: {
        return await evaluateJsonConditions({
          condition,
          dataLinks,
          documents,
          selections,
          local,
          history
        });
      }
      case CONDITION_TYPES.HAS_DOCUMENTS: {
        return await evaluateHasDocumentsCondition({
          condition,
          dataLinks,
          documents,
          selections,
          local,
          history
        });
      }
      case CONDITION_TYPES.FIRST_DOCUMENT_IS_SELECTED: {
        const docs = documents[condition.conditionDataLinkId].documents;
        if (!docs) {
          return false;
        } else if (!selections[condition.conditionDataLinkId]?.documentId) {
          return false;
        }

        return (
          docs.findIndex(
            d => d.id === selections[condition.conditionDataLinkId].documentId
          ) === 0
        );
      }
      case CONDITION_TYPES.LAST_DOCUMENT_IS_SELECTED: {
        const docs = documents[condition.conditionDataLinkId].documents;
        if (!docs) {
          return false;
        } else if (!selections[condition.conditionDataLinkId]?.documentId) {
          return false;
        }

        const index = docs.findIndex(
          d => d.id === selections[condition.conditionDataLinkId]?.documentId
        );

        return index === docs.length - 1;
      }
      default:
        return await evaluateOtherCondition({
          condition,
          documents,
          selections,
          local,
          history
        });
    }
  };

  const evaluateDataLinkCondition = async ({
    condition,
    documents,
    dataLinks,
    selections,
    local,
    history
  } = {}) => {
    // conditionDataLinkId
    // conditionDataLinkField
    // conditionDataLinkOperator
    // conditionDataLinkValue
    let error;

    if (
      !condition.conditionDataLinkId ||
      condition.conditionDataLinkId.trim().length === 0
    ) {
      error =
        'a data link must be selected to evaluate a condition type of "data link"';
    } else if (
      !condition.conditionDataLinkField ||
      condition.conditionDataLinkField.trim().length === 0
    ) {
      error =
        'a data link field must be selected to evaluate a condition type of "data link"';
    } else if (
      !condition.conditionDataLinkOperator ||
      condition.conditionDataLinkOperator.trim().length === 0
    ) {
      error =
        'a data link field operator must be selected to evaluate a condition type of "data link"';
    } else if (!dataLinks || dataLinks?.length === 0) {
      error =
        'there are no data links in context to use with this condition evaluation';
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const contentType = dataLinks.find(
      d => d.id === condition.conditionDataLinkId
    )?.contentType;

    if (!contentType) {
      return {
        matched: false,
        error: `no content type could be determined for data link with id ${condition.conditionDataLinkId}`
      };
    }

    let document = documents[condition.conditionDataLinkId]?.documents.find(
      d => d.id === selections[condition.conditionDataLinkId]?.documentId
    );

    if (!document && contentType.type === 'user') {
      document = user;
    }

    if (!document) {
      return {
        matched: false,
        error: `there is no selected document for data link with id "${condition.conditionDataLinkId}"`
      };
    }

    const value = parse({
      markup: condition.conditionDataLinkValue,
      documents,
      selections,
      user,
      local,
      actionSequenceExecutionHistory: history
    });

    const matched = conditionMatches({
      condition: {
        field: condition.conditionDataLinkField,
        operator: condition.conditionDataLinkOperator,
        value
      },
      contentType,
      document
    });

    return {
      matched
    };
  };

  const evaluateValidationCondition = async ({
    condition,
    documents,
    selections,
    local,
    history
  } = {}) => {
    // conditionValidationDataLinkId
    // conditionValidationField
    // conditionValidationFieldType i.e. string, number,
    // conditionValidationOperator
    // conditionValidationComparisionValue
    // conditionValidationValue
    let error;

    if (
      !condition.conditionValidationField ||
      condition.conditionValidationField.trim().length === 0
    ) {
      error =
        'a field must be selected to evaluate a condition type of "validation"';
    } else if (
      !condition.conditionValidationOperator ||
      condition.conditionValidationOperator.trim().length === 0
    ) {
      error =
        'a validation field operator must be selected to evaluate a condition type of "validation"';
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const comparisionValue = parse({
      markup: condition.conditionValidationComparisionValue,
      documents,
      selections,
      user,
      local,
      actionSequenceExecutionHistory: history
    });

    const document = {};
    document[condition.conditionValidationField] =
      condition.conditionValidationValue;

    const matched = conditionMatches({
      condition: {
        field: condition.conditionValidationField,
        operator: condition.conditionValidationOperator,
        value: comparisionValue
      },
      contentType: {
        fields: [
          {
            name: condition.conditionValidationField,
            type: condition.conditionValidationFieldType
          }
        ]
      },
      document
    });

    return {
      matched
    };
  };

  const evaluateFormValueCondition = async ({
    condition,
    documents,
    selections,
    local,
    history
  } = {}) => {
    //conditionDisplayElementId
    //conditionFormOperator
    //conditionFormCurrentValue
    //conditionFormValue <-- the value to compare against
    let error;

    if (
      !condition.conditionDisplayElementId ||
      condition.conditionDisplayElementId.trim().length === 0
    ) {
      error =
        'a html element id must be specified to evaluate a condition type of "formValueIsMatched"';
    } else if (
      !condition.conditionFormOperator ||
      condition.conditionFormOperator.trim().length === 0
    ) {
      error =
        'an operator must be specified to evaluate a condition type of "formValueIsMatched"';
    } else if (
      !condition.conditionFormValue ||
      condition.conditionFormValue.trim().length === 0
    ) {
      error =
        'a form value to compare against must be supplied to evaluate a condition type of "formValueIsMatched"';
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const comparisionValue = parse({
      markup: condition.conditionFormValue,
      documents,
      selections,
      user,
      local,
      actionSequenceExecutionHistory: history
    });

    const document = {};
    document[condition.conditionDisplayElementId] =
      condition.conditionFormCurrentValue;

    const matched = conditionMatches({
      condition: {
        field: `${condition.conditionDisplayElementId}.value`,
        operator: condition.conditionFormOperator,
        value: comparisionValue
      },
      contentType: { fields: [] },
      document,
      ignoreField: true
    });

    return {
      matched
    };
  };

  const evaluateHasDocumentsCondition = async ({
    condition,
    documents,
    selections,
    dataLinks,
    local,
    history
  } = {}) => {
    // conditionDataLinkId

    let error;

    if (
      !condition.conditionDataLinkId ||
      condition.conditionDataLinkId.trim().length === 0
    ) {
      error =
        'a data link must be selected to evaluate a condition type of "has documents"';
    } else if (
      !dataLinks ||
      dataLinks?.length === 0 ||
      !dataLinks.find(d => d.id === condition.conditionDataLinkId)
    ) {
      error = `there is no data link with id "${condition.conditionDataLinkId}" to use with this condition evaluation`;
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const docs = documents[condition.conditionDataLinkId]?.documents || [];

    if (!condition.conditionFields || condition.conditionFields?.length === 0) {
      return {
        matched: docs.length > 0
      };
    }

    const contentType = dataLinks.find(
      d => d.id === condition.conditionDataLinkId
    )?.contentType;

    if (!contentType) {
      return {
        matched: false,
        error: `no content type was found for data link id ${condition.conditionDataLinkId}`
      };
    }

    const matched = docs.some(doc => {
      return condition.conditionFields.every(field => {
        const value = parse({
          markup: field.value,
          documents,
          selections,
          user,
          local,
          actionSequenceExecutionHistory: history
        });

        return conditionMatches({
          condition: {
            field: field.name,
            operator: field.operator,
            value
          },
          document: doc,
          contentType
        });
      });
    });

    return {
      matched
    };
  };

  const evaluateLocalStoreCondition = async ({
    condition,
    documents,
    selections,
    local,
    history
  } = {}) => {
    // conditionLocalField
    // conditionLocalOperator
    // conditionLocalValue

    let error;

    if (
      !condition.conditionLocalField ||
      condition.conditionLocalField.trim().length === 0
    ) {
      error = 'a local store field must be supplied';
    } else if (
      !condition.conditionLocalOperator ||
      condition.conditionLocalOperator.trim().length === 0
    ) {
      error =
        'an operator must be supplied to compare the local store field value against';
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const value = parse({
      markup: condition.conditionLocalValue,
      documents,
      selections,
      user,
      local,
      actionSequenceExecutionHistory: history
    });

    const contentType = {
      fields: []
    };

    if (local && Object.keys(local).length > 0) {
      Object.keys(local).forEach(key => {
        if (!_.isObject(local[key])) {
          contentType.fields.push({
            name: key,
            type: !isNaN(local[key]) ? 'number' : 'string' // TODO: this could be a problem if we store non-string types in the local store, but how will we know, so try with isnan at best
          });
        }
      });
    }

    const matched = conditionMatches({
      condition: {
        field: condition.conditionLocalField,
        operator: condition.conditionLocalOperator,
        value
      },
      document: local,
      contentType,
      ignoreField:
        contentType.fields.findIndex(
          f => f.name === condition.conditionLocalField
        ) === -1
    });

    return {
      matched
    };
  };

  const evaluateActionSequencerResultsCondition = async ({
    condition,
    documents,
    selections,
    local,
    history,
    sequencer
  } = {}) => {
    // conditionSequencerField
    // conditionSequencerOperator
    // conditionSequencerValue

    let error;

    if (
      !condition.conditionSequencerField ||
      condition.conditionSequencerField.trim().length === 0
    ) {
      error = 'a field from the action sequencer results must be supplied';
    } else if (
      !condition.conditionSequencerOperator ||
      condition.conditionSequencerOperator.trim().length === 0
    ) {
      error =
        'an operator must be supplied to compare the action sequencer result field value against';
    }

    if (error) {
      return {
        matched: false,
        error
      };
    }

    const value = parse({
      markup: condition.conditionSequencerValue,
      documents,
      selections,
      user,
      local,
      actionSequenceExecutionHistory:
        history || sequencer?.results || actionSequencerResults
    });

    const contentType = {
      fields: []
    };

    if (local && Object.keys(local).length > 0) {
      Object.keys(local).forEach(key => {
        if (!_.isObject(local[key])) {
          contentType.fields.push({
            name: key,
            type: !isNaN(local[key]) ? 'number' : 'string' // TODO: this could be a problem if we store non-string types in the local store, but how will we know, so try with isnan at best
          });
        }
      });
    }

    const matched = conditionMatches({
      condition: {
        field: condition.conditionSequencerField,
        operator: condition.conditionLocalOperator,
        value
      },
      document: sequencer?.results || actionSequencerResults,
      contentType,
      ignoreField:
        contentType.fields.findIndex(
          f => f.name === condition.conditionSequencerField
        ) === -1
    });

    return {
      matched
    };
  };

  const evaluateJsonConditions = async ({
    condition,
    documents,
    selections,
    local,
    history,
    dataLinks
  } = {}) => {
    // conditionJson
    try {
      const conditions = JSON.parse(condition.conditionJson);

      if (conditions?.length === 0) {
        return {
          matched: false,
          error: 'no conditions were supplied in the json'
        };
      }

      return {
        matched: conditions.every(condition => {
          const contentType = dataLinks.find(d => d.id === condition.dataLinkId)
            ?.contentType;

          if (!contentType && condition.dataLinkId !== 'user') {
            return false;
          }

          const doc =
            condition.dataLinkId === 'user'
              ? user
              : documents[condition.dataLinkId]?.documents.find(
                  d => d.id === selections[condition.dataLinkId]?.documentId
                );
          if (doc) {
            condition.value = parse({
              markup: condition.value,
              documents,
              selections,
              user,
              local,
              actionSequenceExecutionHistory: history
            });

            const cond = { ...condition };
            delete cond.dataLinkId;

            return conditionMatches({
              condition: cond,
              document: doc,
              contentType
            });
          } else {
            return false;
          }
        })
      };
    } catch {
      return {
        matched: false,
        error: 'unable to parse json'
      };
    }
  };

  const evaluateOtherCondition = async ({
    condition,
    documents,
    selections,
    local,
    history
  } = {}) => {
    // conditionValueSource: local, static, datalink
    // conditionLocalValueField
    // conditionStaticValue
    // conditionValueDataLinkId
    // conditionValueDataLinkField

    let value;

    if (
      condition.conditionType !== CONDITION_TYPES.LOGGED_IN &&
      condition.conditionType !== CONDITION_TYPES.LOGGED_OUT
    ) {
      if (
        !condition.conditionValueSource ||
        !['local', 'static', 'datalink'].some(
          c => c === condition.conditionValueSource
        )
      ) {
        return {
          matched: false,
          error: 'a condition value source must be selected'
        };
      } else if (
        condition.conditionValueSource === 'local' &&
        (!condition.conditionLocalValueField ||
          condition.conditionLocalValueField?.trim().length === 0)
      ) {
        return {
          matched: false,
          error: 'a local value field must be supplied'
        };
      } else if (condition.conditionValueSource === 'datalink') {
        if (
          !condition.conditionValueDataLinkId ||
          condition.conditionValueDataLinkId?.trim().length === 0
        ) {
          return {
            matched: false,
            error: 'a data link must be supplied'
          };
        } else if (
          !condition.conditionValueDataLinkField ||
          condition.conditionValueDataLinkField?.trim().length === 0
        ) {
          return {
            matched: false,
            error: 'a data link field must be supplied'
          };
        }
      } else if (
        [CONDITION_TYPES.HAS_CLAIM, CONDITION_TYPES.DOES_NOT_HAVE_CLAIM].some(
          c => condition.conditionType === c
        ) &&
        (!condition.conditionClaimName ||
          condition.conditionClaimName?.trim().length === 0)
      ) {
        return {
          matched: false,
          error: 'a claim name must be supplied for a condition of this type'
        };
      }

      if (condition.conditionValueSource === 'local') {
        value = local[condition.conditionLocalValueField];
      } else if (condition.conditionValueSource === 'static') {
        value = condition.conditionStaticValue;
      } else {
        const document = documents[
          condition.conditionValueDataLinkId
        ]?.documents.find(
          d =>
            d.id === selections[condition.conditionValueDataLinkId]?.documentId
        );

        if (!document) {
          return {
            matched: false,
            error: `there is no selected document for data link with id "${condition.conditionValueDataLinkId}"`
          };
        }

        value = document[condition.conditionValueDataLinkField];
      }
      value = parse({
        markup: value,
        documents,
        selections,
        user,
        local,
        actionSequenceExecutionHistory: history
      });
    }

    const matched = await matchConditionType(condition, value);

    return {
      matched
    };
  };

  const matchConditionType = async (condition, value) => {
    let matched = false;
    switch (condition.conditionType) {
      case CONDITION_TYPES.HAS_STRIPE_ROLE: {
        const matchRoles = value?.split ? value.split(',') : [];
        matched = matchRoles.some(r => r === user?.claims?.stripeRole);
        break;
      }
      case CONDITION_TYPES.DOES_NOT_HAVE_STRIPE_ROLE: {
        const matchRoles = value?.split ? value.split(',') : [];
        matched = !matchRoles.some(r => r === user?.claims?.stripeRole);
        break;
      }
      case CONDITION_TYPES.HAS_ROLE: {
        const matchRoles = value?.split ? value.split(',') : [];
        matched = matchRoles.some(r => r === user?.role);
        break;
      }
      case CONDITION_TYPES.DOES_NOT_HAVE_ROLE: {
        const matchRoles = value?.split ? value.split(',') : [];
        matched = !matchRoles.some(r => r === user?.role);
        break;
      }
      case CONDITION_TYPES.HAS_CLAIM: {
        if (user?.claims) {
          const matchClaims = value?.split ? value.split(',') : [];
          matched = matchClaims.some(
            r => user?.claims[condition.conditionClaimName] === r
          );
        }
        break;
      }
      case CONDITION_TYPES.DOES_NOT_HAVE_CLAIM:
        if (user?.claims) {
          const matchClaims = value?.split ? value.split(',') : [];
          matched = !matchClaims.some(
            r => user?.claims[condition.conditionClaimName] === r
          );
        }
        break;
      case CONDITION_TYPES.HAS_PRODUCT_ID:
        //https://docs.revenuecat.com/docs/customer-info
        matched = await hasProductOwnership(value);
        break;
      case CONDITION_TYPES.DOES_NOT_HAVE_PRODUCT_ID:
        matched = await !hasProductOwnership(value);
        break;
      case CONDITION_TYPES.HAS_ACTIVE_SUBSCRIPTION:
        //https://docs.revenuecat.com/docs/customer-info
        matched = await hasActiveSubscription(value);
        break;
      case CONDITION_TYPES.DOES_NOT_ACTIVE_SUBSCRIPTION:
        matched = await !hasActiveSubscription(value);
        break;
      case CONDITION_TYPES.LOGGED_IN:
        matched =
          user?.validUser === true ||
          (authenticatedUser?.uid !== undefined && !authenticatedUserLoading);
        break;
      case CONDITION_TYPES.LOGGED_OUT:
        matched =
          user === undefined ||
          !user?.validUser ||
          authenticatedUser?.uid === undefined;
        break;
      default:
        break;
    }

    return matched;
  };

  return {
    evaluate
  };
};

export default useConditions;
